import { Outlet, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import KeepAliveRouteOutlet from 'keepalive-for-react-router';
import { resolveRouteTabMeta } from '@/router/routeMeta';
import { isTabRouteKeepAliveEnabled } from '@/utils/tabKeepAliveEnv';
import { useStore } from '@/store';

/**
 * 桌面布局内「业务内容区」路由出口。
 *
 * - 默认与 {@link Outlet} 一致（未开环境变量）。
 * - `REACT_APP_TAB_KEEPALIVE=1` 且当前路由 `cacheStrategy === 'instance'` 时，使用
 *   `keepalive-for-react-router`，`activeCacheKey` 取当前 URL 解析的 `tabKey`（与地址栏同步，避免先改 activeKey 再 navigate 的一帧错位）。
 * - `state` / `none` 仍走普通 Outlet，由 `tabStore.stateCache` 或每次挂载处理（与方案文档辅路径一致）。
 */
function TabKeepAliveShellComponent() {
  const location = useLocation();
  const tabStore = useStore('tab');
  const fullPath = `${location.pathname}${location.search}`;
  const meta = resolveRouteTabMeta(
    location.pathname,
    location.search,
    fullPath
  );

  if (!isTabRouteKeepAliveEnabled() || meta.cacheStrategy !== 'instance') {
    return <Outlet />;
  }

  const activeCacheKey = meta.keepAliveCacheKey || meta.tabKey || fullPath;
  const includeKeys = Array.from(
    new Set([...tabStore.getKeepAliveIncludeKeys(), activeCacheKey])
  );

  return (
    <KeepAliveRouteOutlet
      activeCacheKey={activeCacheKey}
      include={includeKeys}
      max={10}
    />
  );
}

export const TabKeepAliveShell = observer(TabKeepAliveShellComponent);
